// src/pages/profile-settings/doctor/practiceTeamMemberAccess.js
import React from "react";

import { useAuth } from "@/contexts/AuthContext.jsx";
import {
  PHARMACY_MEMBER_ACCOUNT_UPDATED_EVENT,
  getPharmacyMemberAccount,
  getPharmacyMemberPhotoUrl,
} from "@/services/pharmacyMemberAccountService.js";
import { callFunctionRaw, resolveFunctionAccessToken } from "@/lib/callFunction.js";
import { getPracticeTeamNavigationSection } from "./practiceTeamWorkspaceModel.js";

function safeText(value) {
  return String(value || "").trim();
}

const memberPhotoUrlCache = new Map();

function memberPhotoCacheKey(userId = "", path = "") {
  return `${safeText(userId)}:${safeText(path)}`;
}

export function readCachedPracticeTeamMemberPhotoUrl({ userId = "", profilePhotoPath = "" } = {}) {
  const cleanUserId = safeText(userId);
  if (!cleanUserId) return "";

  const cleanPath = safeText(profilePhotoPath);
  if (cleanPath) {
    return safeText(memberPhotoUrlCache.get(memberPhotoCacheKey(cleanUserId, cleanPath)));
  }

  for (const [key, value] of memberPhotoUrlCache.entries()) {
    if (key.startsWith(`${cleanUserId}:`) && safeText(value)) return value;
  }
  return "";
}

export function cachePracticeTeamMemberPhotoUrl({ userId = "", profilePhotoPath = "", photoUrl = "" } = {}) {
  const cleanUserId = safeText(userId);
  const cleanPath = safeText(profilePhotoPath);
  const cleanUrl = safeText(photoUrl);
  if (!cleanUserId || !cleanPath || !cleanUrl) return;
  memberPhotoUrlCache.set(memberPhotoCacheKey(cleanUserId, cleanPath), cleanUrl);
}

export function clearCachedPracticeTeamMemberPhotoUrl({ userId = "", profilePhotoPath = "" } = {}) {
  const cleanUserId = safeText(userId);
  if (!cleanUserId) return;

  const cleanPath = safeText(profilePhotoPath);
  if (cleanPath) {
    memberPhotoUrlCache.delete(memberPhotoCacheKey(cleanUserId, cleanPath));
    return;
  }

  for (const key of memberPhotoUrlCache.keys()) {
    if (key.startsWith(`${cleanUserId}:`)) memberPhotoUrlCache.delete(key);
  }
}

function readCachedWelcomeInvite(userId = "") {
  try {
    if (typeof window === "undefined") return null;
    const parsed = JSON.parse(window.sessionStorage?.getItem("afrimed:practice-team:welcome-invite") || "null");
    if (!parsed?.invite?.id) return null;
    if (userId && parsed.user_id && parsed.user_id !== userId) return null;
    return parsed.invite;
  } catch {
    return null;
  }
}

function normalizeRole(value = "") {
  return String(value || "").trim().toLowerCase();
}

function isActiveInvite(invite = null) {
  return normalizeRole(invite?.status) === "active";
}

function inviteBelongsToUser(invite = null, user = null) {
  const userId = safeText(user?.id);
  const email = safeText(user?.email).toLowerCase();
  const inviteUserId = safeText(invite?.user_id);
  const inviteEmail = safeText(invite?.email).toLowerCase();
  return Boolean(
    (userId && inviteUserId && userId === inviteUserId) ||
      (email && inviteEmail && email === inviteEmail),
  );
}

async function resolveVerifiedPracticeTeamInvite() {
  const accessToken = await resolveFunctionAccessToken({
    refreshIfMissing: true,
    forceRefresh: false,
  });
  if (!safeText(accessToken)) return null;

  const result = await callFunctionRaw(
    "practice-team-invite-lifecycle",
    { action: "welcome", member_id: "" },
    { accessToken, timeoutMs: 10000, preferFreshSession: false },
  );
  return result?.invite || null;
}

export function usePracticeTeamAccess({ requiredSectionId = "" } = {}) {
  const auth = useAuth() || {};
  const user = auth?.user || null;
  const userId = user?.id || "";
  const userEmail = safeText(user?.email).toLowerCase();
  const authInvite = auth?.profile?.practice_team_invite || null;
  const cachedInvite = React.useMemo(() => readCachedWelcomeInvite(userId), [userId]);
  const initialInvite = authInvite || cachedInvite || null;
  const [verifiedInvite, setVerifiedInvite] = React.useState(() => initialInvite);
  const [membershipResolving, setMembershipResolving] = React.useState(
    () => Boolean(userId) && !isActiveInvite(initialInvite),
  );

  React.useEffect(() => {
    let alive = true;
    const alreadyVerified = isActiveInvite(authInvite) && inviteBelongsToUser(authInvite, user);
    const cachedVerified = isActiveInvite(cachedInvite) && inviteBelongsToUser(cachedInvite, user);

    if (!userId) {
      setVerifiedInvite(null);
      setMembershipResolving(false);
      return () => {
        alive = false;
      };
    }

    if (alreadyVerified || cachedVerified) {
      setVerifiedInvite(authInvite || cachedInvite);
      setMembershipResolving(false);
      return () => {
        alive = false;
      };
    }

    setMembershipResolving(true);
    resolveVerifiedPracticeTeamInvite()
      .then((nextInvite) => {
        if (!alive) return;
        if (isActiveInvite(nextInvite) && inviteBelongsToUser(nextInvite, user)) {
          setVerifiedInvite(nextInvite);
          try {
            window.sessionStorage?.setItem(
              "afrimed:practice-team:welcome-invite",
              JSON.stringify({ user_id: userId, invite: nextInvite, cached_at: Date.now() }),
            );
          } catch {
            // The member lookup remains the authority; caching only prevents a route flash.
          }
          void auth?.refreshProfile?.();
        } else {
          setVerifiedInvite(null);
        }
      })
      .catch(() => {
        if (alive) setVerifiedInvite(null);
      })
      .finally(() => {
        if (alive) setMembershipResolving(false);
      });

    return () => {
      alive = false;
    };
  }, [authInvite, auth?.refreshProfile, cachedInvite, userEmail, userId]);

  const invite =
    (isActiveInvite(authInvite) && inviteBelongsToUser(authInvite, user) && authInvite) ||
    (isActiveInvite(verifiedInvite) && inviteBelongsToUser(verifiedInvite, user) && verifiedInvite) ||
    (isActiveInvite(cachedInvite) && inviteBelongsToUser(cachedInvite, user) && cachedInvite) ||
    null;
  const resolving = auth?.loading || auth?.profileResolved === false || membershipResolving;
  const section = requiredSectionId
    ? getPracticeTeamNavigationSection(invite?.role, requiredSectionId)
    : null;

  return {
    auth,
    invite,
    section,
    resolving,
    // The Edge Function has already authenticated the member and verified
    // active membership. Do not require a second, potentially stale profile
    // role snapshot before rendering the delegated workspace.
    allowed: isActiveInvite(invite) && (!requiredSectionId || section?.enabled === true),
  };
}

export function usePracticeTeamMemberAccount({ user = null, identity = null } = {}) {
  const userId = safeText(user?.id);
  const email = safeText(identity?.email || user?.email).toLowerCase();
  const fullName = safeText(identity?.displayName || user?.user_metadata?.full_name);
  const [account, setAccount] = React.useState(null);
  const [photoUrl, setPhotoUrl] = React.useState(() => readCachedPracticeTeamMemberPhotoUrl({ userId }));
  const [loading, setLoading] = React.useState(Boolean(userId));

  const loadAccount = React.useCallback(async () => {
    if (!userId) {
      setAccount(null);
      setPhotoUrl("");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const nextAccount = await getPharmacyMemberAccount({
        userId,
        email,
        fullName,
      });
      setAccount(nextAccount);

      if (safeText(nextAccount?.profile_photo_path)) {
        const cached = readCachedPracticeTeamMemberPhotoUrl({
          userId,
          profilePhotoPath: nextAccount.profile_photo_path,
        });
        if (cached) setPhotoUrl(cached);
        const signed = await getPharmacyMemberPhotoUrl(nextAccount.profile_photo_path);
        if (safeText(signed)) {
          cachePracticeTeamMemberPhotoUrl({
            userId,
            profilePhotoPath: nextAccount.profile_photo_path,
            photoUrl: signed,
          });
          setPhotoUrl(signed);
        }
      } else {
        clearCachedPracticeTeamMemberPhotoUrl({ userId });
        setPhotoUrl("");
      }
    } catch {
      setAccount(null);
      setPhotoUrl((current) => current || readCachedPracticeTeamMemberPhotoUrl({ userId }));
    } finally {
      setLoading(false);
    }
  }, [email, fullName, userId]);

  React.useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;
    window.addEventListener(PHARMACY_MEMBER_ACCOUNT_UPDATED_EVENT, loadAccount);
    return () => window.removeEventListener(PHARMACY_MEMBER_ACCOUNT_UPDATED_EVENT, loadAccount);
  }, [loadAccount]);

  return {
    account,
    photoUrl,
    loading,
    refresh: loadAccount,
  };
}
